import { MoreHoriz } from "@mui/icons-material";
import {
  AspectRatio,
  Box,
  CardContent,
  CardOverflow,
  Chip,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Typography,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { HTTPError } from "griptonite-api-client/client";
import { ApiQuest, ApiQuestType } from "griptonite-api-client/types";

import { useDuplicateQuestMutation } from "@/hooks/quests";
import { useCurrentVenueQuery } from "@/hooks/venues";
import formatPeriod from "@/utils/formatPeriod";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import { ChallengeStatusChip } from "../ChallengeCard";
import { CardLink } from "../Links";
import { useModal } from "../modals/useModal";

const questTypeLabels: Record<ApiQuestType, string> = {
  WORKOUT: "Workout",
  LEADERBOARD_POINTS: "Points pursuit",
};

export const QuestCard = ({ quest }: { quest: ApiQuest }) => {
  const { data: venue } = useCurrentVenueQuery();
  const navigate = useNavigate();
  const { display: displayModal } = useModal();
  const duplicateMutation = useDuplicateQuestMutation();

  const onDuplicate = () =>
    duplicateMutation.mutate(quest.uuid, {
      // The copy is a hidden draft, so land on it to let the staff member adjust it.
      onSuccess: (copy) =>
        navigate({
          from: "/gym/$venueUuid/loyalty",
          to: "./quests/$questUuid",
          params: { questUuid: copy.uuid },
        }),
      onError: (error) => {
        // The only conflict the API reports is a venue with no free month in the next year.
        alert(
          error instanceof HTTPError && error.response.status === 409
            ? "Every one of the next 12 months already has a points quest, so this quest can't be duplicated."
            : "Failed to duplicate quest. Please try again or contact support",
        );
      },
    });

  return (
    <CardLink
      data-uuid={quest.uuid}
      from="/gym/$venueUuid/loyalty"
      to="./quests/$questUuid"
      params={{ questUuid: quest.uuid }}
      sx={{ borderColor: "neutral.200", textDecoration: "none" }}
    >
      <CardOverflow>
        <AspectRatio ratio="2" objectFit="cover">
          {quest.coverImage ? (
            <img
              alt=""
              src={
                getThumbnailUrl(quest.coverImage.url, {
                  size: 600,
                  mode: "resize",
                }) ?? undefined
              }
            />
          ) : (
            <Box sx={{ backgroundColor: "neutral.100" }} />
          )}
        </AspectRatio>
        <Chip
          variant="soft"
          color="neutral"
          size="md"
          sx={{
            position: "absolute",
            right: 8,
            bottom: 8,
            fontWeight: "bold",
            backgroundColor: "white",
          }}
        >
          {questTypeLabels[quest.type] ?? quest.type}
        </Chip>
        <Dropdown>
          <MenuButton
            sx={{ position: "absolute", top: 8, right: 8, borderRadius: "100%" }}
            slots={{ root: IconButton }}
            slotProps={{ root: { size: "sm", color: "neutral", variant: "soft" } }}
            // The card is a link, so stop the click from navigating.
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <MoreHoriz />
          </MenuButton>
          <Menu size="sm">
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                navigate({
                  from: "/gym/$venueUuid/loyalty",
                  to: "./quests/$questUuid",
                  params: { questUuid: quest.uuid },
                });
              }}
            >
              Edit
            </MenuItem>
            {/* Only points quests can be created, so only they can be copied. */}
            <MenuItem
              disabled={quest.type !== "LEADERBOARD_POINTS" || duplicateMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                onDuplicate();
              }}
            >
              Duplicate
            </MenuItem>
            <MenuItem
              color="danger"
              onClick={(e) => {
                e.preventDefault();
                displayModal("CONFIRM_DELETE_QUEST", { quest });
              }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Dropdown>
        {quest.isHidden ? (
          <Chip
            variant="soft"
            color="warning"
            size="sm"
            sx={{ position: "absolute", left: 8, top: 8, fontWeight: "bold" }}
          >
            Hidden
          </Chip>
        ) : null}
      </CardOverflow>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography level="title-md" fontWeight="bold">
            {quest.name}
          </Typography>
          <ChallengeStatusChip status={quest.status} />
        </Box>
        <Typography textColor="neutral.500" level="body-sm">
          {formatPeriod(quest, venue?.timezone || "UTC")}
        </Typography>
      </CardContent>
    </CardLink>
  );
};
